import React, { useEffect, useMemo, useRef } from "react";
import {
  applyLiveFxVars,
  fxOpacities,
  galaxyPatternVars,
  poseStyleFromVars,
  resolveFoilStyleId,
  staticFxPose,
} from "./aiShine";
import { topographyPattern } from "./aiTopography";
import { observeFoilVisibility } from "./aiFoilVisibility";

function classSel(className) {
  return className ? `.${CSS.escape(className)}` : null;
}

function findArtMedia(host, styles) {
  if (!host?.parentElement) return null;
  const root = host.parentElement;
  const classes = [
    styles.cardArtMedia,
    styles.cardExamArtExpandedImage,
    styles.cardExamArtMedia,
    styles.cardExamArtImage,
    styles.toastCardArtMedia,
    styles.equipArtMedia,
    styles.boardActionStripArtFill,
    styles.slotAbilityArt,
  ].filter(Boolean);
  for (let i = 0; i < classes.length; i += 1) {
    const node = root.getElementsByClassName(classes[i])[0];
    if (node) return node;
  }
  return root.querySelector("img");
}

function clearFoil3dClones(container) {
  if (!container) return;
  container
    .querySelectorAll("[data-ai-foil-3d]")
    .forEach((node) => node.remove());
}

const FOIL_3D_BASE_FILTER =
  "grayscale(1) brightness(0.1) saturate(0.3) contrast(1)";

function createFoil3dClone(media, className) {
  const isImg = media.tagName === "IMG";
  const clone = document.createElement(isImg ? "img" : "div");
  clone.className = className;
  clone.setAttribute("aria-hidden", "true");
  clone.setAttribute("data-ai-foil-3d", "1");
  if (isImg) {
    clone.alt = "";
    clone.decoding = "async";
    clone.draggable = false;
    clone.src = media.currentSrc || media.src || "";
  } else {
    const cs = getComputedStyle(media);
    clone.style.backgroundImage =
      media.style.backgroundImage || cs.backgroundImage;
    clone.style.backgroundSize =
      media.style.backgroundSize || cs.backgroundSize || "cover";
    clone.style.backgroundPosition =
      media.style.backgroundPosition || cs.backgroundPosition || "center";
    clone.style.backgroundRepeat = "no-repeat";
  }
  return clone;
}

function applyFoil3dOffsets(red, blue, green, mx, my) {
  if (!red || !blue || !green) return;
  const x = Math.min(1, Math.max(0, Number(mx) || 0.5));
  const y = Math.min(1, Math.max(0, Number(my) || 0.5));
  const ox = ((x - 0.5) * 5).toFixed(2);
  const oy = ((y - 0.5) * 5).toFixed(2);
  red.style.transform = `translate(calc(-2.8% - ${ox}px), calc(1.8% + ${oy}px))`;
  blue.style.transform = `translate(calc(-2.8% - ${ox}px), calc(-1.8% - ${oy}px))`;
  green.style.transform = `translate(calc(2.8% + ${ox}px), calc(1.8% + ${oy}px))`;
}

function findPointerHost(overlay, styles) {
  if (!overlay) return null;
  return (
    (classSel(styles.aiHoloCard) &&
      overlay.closest(classSel(styles.aiHoloCard))) ||
    (classSel(styles.cardExamArt) &&
      overlay.closest(classSel(styles.cardExamArt))) ||
    (classSel(styles.toastCard) && overlay.closest(classSel(styles.toastCard))) ||
    (classSel(styles.card) && overlay.closest(classSel(styles.card))) ||
    (classSel(styles.equipBlock) &&
      overlay.closest(classSel(styles.equipBlock))) ||
    (classSel(styles.boardActionStrip) &&
      overlay.closest(classSel(styles.boardActionStrip))) ||
    (classSel(styles.abilitySlotRect) &&
      overlay.closest(classSel(styles.abilitySlotRect))) ||
    (classSel(styles.abilitySlotCircle) &&
      overlay.closest(classSel(styles.abilitySlotCircle))) ||
    overlay.parentElement
  );
}

function TopographyLines({ pattern, styles, className = "" }) {
  return (
    <svg
      className={[styles.holoTopographySvg, className].filter(Boolean).join(" ")}
      viewBox={`0 0 ${pattern.size} ${pattern.size}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      {pattern.lines.map((line) => (
        <path
          key={line.id}
          className={[
            styles.holoTopographyLine,
            line.major ? styles.holoTopographyLineMajor : "",
          ]
            .filter(Boolean)
            .join(" ")}
          d={line.d}
        />
      ))}
    </svg>
  );
}

// Rising Star: every few seconds a star streaks from the top right toward the
// bottom left (randomized, slightly curved) and drops a fading sparkle trail.
// Elements are created per launch and removed when their animation ends.
const RISING_STAR_MIN_DELAY_MS = 2600;
const RISING_STAR_MAX_DELAY_MS = 5200;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

function launchRisingStar(layer, styles, { sparkleCount = 14 } = {}) {
  if (!layer || typeof layer.animate !== "function") return;
  const start = { x: randomBetween(78, 108), y: randomBetween(-12, 14) };
  const end = { x: randomBetween(-12, 22), y: randomBetween(84, 112) };
  // Bend perpendicular to the path for a slight arc (either side).
  const bend = randomBetween(-9, 9);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy) || 1;
  const normal = { x: -dy / length, y: dx / length };
  const pointAt = (t) => {
    const arc = Math.sin(Math.PI * t) * bend;
    return {
      x: start.x + dx * t + normal.x * arc,
      y: start.y + dy * t + normal.y * arc,
    };
  };
  const duration = randomBetween(480, 720);
  // A slight turn reads as a glint; a full spin looked cartoonish.
  const tilt = randomBetween(-12, 12);
  const spin = randomBetween(18, 40) * (Math.random() < 0.5 ? -1 : 1);

  const star = document.createElement("span");
  star.className = styles.holoRisingStar;
  layer.appendChild(star);
  const steps = [0, 0.15, 0.5, 0.85, 1];
  const starAnimation = star.animate(
    steps.map((t) => {
      const point = pointAt(t);
      return {
        left: `${point.x}%`,
        top: `${point.y}%`,
        opacity: t === 0 || t === 1 ? 0 : 1,
        transform: `translate(-50%, -50%) rotate(${tilt + spin * t}deg) scale(${
          0.6 + 0.45 * Math.sin(Math.PI * Math.min(1, t * 1.15))
        })`,
        offset: t,
      };
    }),
    { duration, easing: "cubic-bezier(0.3, 0, 0.7, 1)", fill: "both" },
  );
  starAnimation.onfinish = () => star.remove();
  starAnimation.oncancel = () => star.remove();

  for (let index = 0; index < sparkleCount; index += 1) {
    const t = Math.min(0.96, (index + Math.random() * 0.8) / sparkleCount);
    const point = pointAt(t);
    const sparkle = document.createElement("span");
    sparkle.className = styles.holoRisingSparkle;
    const size = randomBetween(0.55, 1.25);
    const x = point.x + randomBetween(-2.2, 2.2);
    const y = point.y + randomBetween(-2.2, 2.2);
    const drift = randomBetween(1.5, 4.5);
    sparkle.style.left = `${x}%`;
    sparkle.style.top = `${y}%`;
    layer.appendChild(sparkle);
    const rotate = randomBetween(-20, 20);
    const at = (dy, scale) =>
      `translate(-50%, calc(-50% + ${dy}px)) rotate(${rotate}deg) scale(${scale})`;
    // Pops in behind the star, twinkles while it lingers, then fades.
    const sparkleAnimation = sparkle.animate(
      [
        { opacity: 0, transform: at(0, 0.2) },
        { opacity: 1, transform: at(0, size), offset: 0.08 },
        { opacity: 0.55, transform: at(drift * 0.3, size * 0.8), offset: 0.3 },
        { opacity: 0.85, transform: at(drift * 0.5, size * 0.9), offset: 0.5 },
        { opacity: 0.4, transform: at(drift * 0.75, size * 0.65), offset: 0.75 },
        { opacity: 0, transform: at(drift, size * 0.25) },
      ],
      {
        duration: randomBetween(1500, 2400),
        delay: duration * t,
        easing: "ease-out",
        fill: "both",
      },
    );
    sparkleAnimation.onfinish = () => sparkle.remove();
    sparkleAnimation.oncancel = () => sparkle.remove();
  }
}

function readPoseFromElement(el, fallback) {
  if (!el) return fallback;
  const cs = getComputedStyle(el);
  const mx = Number.parseFloat(cs.getPropertyValue("--mx"));
  const my = Number.parseFloat(cs.getPropertyValue("--my"));
  return {
    mx: Number.isFinite(mx) ? mx : fallback.mx,
    my: Number.isFinite(my) ? my : fallback.my,
  };
}

export default function AiFoilLayers({
  foilEffect = null,
  cardId = null,
  styles,
  className = "",
  inheritPose = false,
  quality = "maximum",
  animateIdle = false,
}) {
  const hostRef = useRef(null);
  const rippleNoiseRef = useRef(null);
  const risingStarLayerRef = useRef(null);
  const rippleDispRef = useRef(null);
  const visibleRef = useRef(true);
  const resolved = resolveFoilStyleId(foilEffect);
  const pose = useMemo(
    () => staticFxPose(cardId || foilEffect || "base"),
    [cardId, foilEffect],
  );
  const opacities = useMemo(
    () => fxOpacities(resolved, quality),
    [resolved, quality],
  );
  const galaxyVars = useMemo(
    () => (resolved === "galaxy" ? galaxyPatternVars(cardId) : null),
    [resolved, cardId],
  );
  const topography = useMemo(
    () => (resolved === "topography" ? topographyPattern(cardId) : null),
    [resolved, cardId],
  );
  const rippleFilterId = useMemo(() => {
    const raw = String(cardId || foilEffect || "ripple");
    return `ai-ripple-${raw.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  }, [cardId, foilEffect]);
  const rippleSeed =
    ((Number.parseInt(String(cardId || "7").replace(/\D/g, ""), 10) || 7) >>>
      0) %
      997 || 7;
  const trackPointer = quality === "maximum" && !inheritPose;
  const runIdle = trackPointer && animateIdle;
  const hasFrameLoop =
    runIdle ||
    ((resolved === "ripple" || resolved === "3d") &&
      (inheritPose || trackPointer));

  useEffect(() => {
    visibleRef.current = true;
    if (quality === "off" || !hasFrameLoop) return undefined;
    return observeFoilVisibility(hostRef.current, (visible) => {
      visibleRef.current = visible;
    });
  }, [quality, hasFrameLoop]);

  useEffect(() => {
    if (quality === "off") return undefined;
    const overlay = hostRef.current;
    if (!overlay) return undefined;

    const applyPose = (target, mx, my) => {
      applyLiveFxVars(overlay, { mx, my, hue: 48, includeOpacities: false });
      if (target) applyLiveFxVars(target, { mx, my, hue: 48 });
      overlay.style.setProperty("--shine-opacity", String(opacities.shine));
      overlay.style.setProperty("--foil-opacity", String(opacities.foil));
      overlay.style.setProperty("--glare-opacity", String(opacities.glare));
    };

    applyPose(null, pose.mx, pose.my);

    if (inheritPose || !trackPointer) {
      return undefined;
    }

    const pointerHost = findPointerHost(overlay, styles);
    if (!pointerHost) return undefined;

    let raf = 0;
    let interacting = false;
    let t =
      Math.random() * Math.PI * 2 +
      ((((Number.parseInt(String(cardId || "7").replace(/\D/g, ""), 10) ||
        7) >>>
        0) %
        97) /
        97) *
        Math.PI *
        2;

    const onPointerMove = (event) => {
      interacting = true;
      const rect = pointerHost.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const mx = (event.clientX - rect.left) / rect.width;
      const my = (event.clientY - rect.top) / rect.height;
      applyPose(pointerHost, mx, my);
    };

    const onPointerLeave = () => {
      interacting = false;
      if (!runIdle) {
        applyPose(pointerHost, pose.mx, pose.my);
      }
    };

    const idleTick = () => {
      if (!interacting && visibleRef.current) {
        t += 0.028;
        const mx = 0.5 + Math.sin(t) * 0.32;
        const my = 0.5 + Math.cos(t * 0.87) * 0.28;
        applyPose(pointerHost, mx, my);
      }
      raf = requestAnimationFrame(idleTick);
    };

    pointerHost.addEventListener("pointermove", onPointerMove, {
      passive: true,
    });
    pointerHost.addEventListener("pointerleave", onPointerLeave);
    if (runIdle) {
      raf = requestAnimationFrame(idleTick);
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      pointerHost.removeEventListener("pointermove", onPointerMove);
      pointerHost.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [
    trackPointer,
    runIdle,
    inheritPose,
    pose.mx,
    pose.my,
    opacities.shine,
    opacities.foil,
    opacities.glare,
    styles,
    foilEffect,
    cardId,
    quality,
  ]);

  useEffect(() => {
    if (resolved !== "ripple" || quality === "off") return undefined;
    const host = hostRef.current;
    const art = findArtMedia(host, styles);
    if (art) {
      art.style.filter = `url(#${rippleFilterId})`;
      art.style.transform = "scale(1.08)";
    }
    const applyWarp = (mx, my, t = 0) => {
      const noise = rippleNoiseRef.current;
      const disp = rippleDispRef.current;
      if (!noise || !disp) return;
      const x = Math.min(1, Math.max(0, Number(mx) || 0.5));
      const y = Math.min(1, Math.max(0, Number(my) || 0.5));
      const fxFreq = (
        0.01 +
        x * 0.016 +
        Math.sin(t * 2.1) * 0.0028
      ).toFixed(4);
      const fyFreq = (
        0.014 +
        y * 0.018 +
        Math.cos(t * 1.7) * 0.0028
      ).toFixed(4);
      noise.setAttribute("baseFrequency", `${fxFreq} ${fyFreq}`);
      const fromCenter = Math.min(1, Math.hypot(x - 0.5, y - 0.5) / 0.5);
      const artRect = art?.getBoundingClientRect?.();
      const artSpan = artRect
        ? Math.hypot(artRect.width || 0, artRect.height || 0)
        : 220;
      const sizeBoost = Math.max(1, Math.min(4.2, artSpan / 240));
      const baseScale = 22 + fromCenter * 42 + Math.sin(t * 2.4) * 6;
      disp.setAttribute("scale", (baseScale * sizeBoost).toFixed(1));
    };

    const readPose = () => {
      const poseHost =
        (styles.aiHoloFace &&
          host?.closest?.(`.${CSS.escape(styles.aiHoloFace)}`)) ||
        (styles.aiHoloCard &&
          host?.closest?.(`.${CSS.escape(styles.aiHoloCard)}`)) ||
        host;
      return readPoseFromElement(poseHost, pose);
    };

    if (!inheritPose && !trackPointer) {
      applyWarp(pose.mx, pose.my, 0);
      return () => {
        if (art) {
          art.style.removeProperty("filter");
          art.style.removeProperty("transform");
        }
      };
    }

    let raf = 0;
    const started = performance.now();
    const tick = (now) => {
      if (visibleRef.current) {
        const next = readPose();
        applyWarp(next.mx, next.my, (now - started) / 1000);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      if (art) {
        art.style.removeProperty("filter");
        art.style.removeProperty("transform");
      }
    };
  }, [
    resolved,
    quality,
    inheritPose,
    trackPointer,
    pose.mx,
    pose.my,
    rippleFilterId,
    styles,
  ]);

  useEffect(() => {
    if (resolved !== "3d" || quality === "off") return undefined;
    const host = hostRef.current;
    const art = findArtMedia(host, styles);
    if (!art?.parentElement || !styles.foil3dClone) return undefined;

    const container = art.parentElement;
    let red = null;
    let blue = null;
    let green = null;
    let raf = 0;
    let cancelled = false;

    const readPose = () => {
      const poseHost =
        (styles.aiHoloFace &&
          host?.closest?.(`.${CSS.escape(styles.aiHoloFace)}`)) ||
        (styles.aiHoloCard &&
          host?.closest?.(`.${CSS.escape(styles.aiHoloCard)}`)) ||
        host;
      return readPoseFromElement(poseHost, pose);
    };

    const artHasSource = () => {
      if (art.tagName !== "IMG") return true;
      return Boolean(art.currentSrc || art.getAttribute("src"));
    };

    const syncClones = () => {
      if (cancelled || !artHasSource()) return false;
      clearFoil3dClones(container);
      art.style.filter = FOIL_3D_BASE_FILTER;
      red = createFoil3dClone(
        art,
        [styles.foil3dClone, styles.foil3dCloneRed].filter(Boolean).join(" "),
      );
      blue = createFoil3dClone(
        art,
        [styles.foil3dClone, styles.foil3dCloneBlue].filter(Boolean).join(" "),
      );
      green = createFoil3dClone(
        art,
        [styles.foil3dClone, styles.foil3dCloneGreen]
          .filter(Boolean)
          .join(" "),
      );
      art.insertAdjacentElement("afterend", red);
      red.insertAdjacentElement("afterend", blue);
      blue.insertAdjacentElement("afterend", green);
      const next = readPose();
      applyFoil3dOffsets(red, blue, green, next.mx, next.my);
      return true;
    };

    const onArtReady = () => {
      syncClones();
    };

    art.addEventListener("load", onArtReady);
    const observer =
      typeof MutationObserver === "function"
        ? new MutationObserver(onArtReady)
        : null;
    if (observer && art.tagName === "IMG") {
      observer.observe(art, { attributes: true, attributeFilter: ["src"] });
    }

    syncClones();

    if (!inheritPose && !trackPointer) {
      return () => {
        cancelled = true;
        art.removeEventListener("load", onArtReady);
        observer?.disconnect();
        clearFoil3dClones(container);
        art.style.removeProperty("filter");
      };
    }

    const tick = () => {
      if (red && blue && green && visibleRef.current) {
        const next = readPose();
        applyFoil3dOffsets(red, blue, green, next.mx, next.my);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      art.removeEventListener("load", onArtReady);
      observer?.disconnect();
      clearFoil3dClones(container);
      art.style.removeProperty("filter");
    };
  }, [
    resolved,
    quality,
    inheritPose,
    trackPointer,
    pose.mx,
    pose.my,
    styles,
  ]);

  useEffect(() => {
    if (resolved !== "rising_star" || quality === "off") return undefined;
    if (prefersReducedMotion()) return undefined;
    const layer = risingStarLayerRef.current;
    if (!layer) return undefined;
    let visible = true;
    const stopObserving = observeFoilVisibility(layer, (next) => {
      visible = next;
    });
    let timer = 0;
    const schedule = (delay) => {
      timer = window.setTimeout(() => {
        const pageVisible =
          typeof document === "undefined" || document.visibilityState !== "hidden";
        if (visible && pageVisible) {
          launchRisingStar(layer, styles, {
            sparkleCount: quality === "maximum" ? 14 : 8,
          });
        }
        schedule(randomBetween(RISING_STAR_MIN_DELAY_MS, RISING_STAR_MAX_DELAY_MS));
      }, delay);
    };
    // Random first launch so a grid of Rising Star cards doesn't fire in sync.
    schedule(randomBetween(400, RISING_STAR_MAX_DELAY_MS));
    return () => {
      window.clearTimeout(timer);
      stopObserving();
      layer.getAnimations?.({ subtree: true }).forEach((animation) =>
        animation.cancel(),
      );
      layer.replaceChildren();
    };
  }, [resolved, quality, styles]);

  if (quality === "off" || !styles) return null;

  const isRainbow = resolved === "rainbow";
  const isGrayscale = resolved === "grayscale";
  const isGalaxy = resolved === "galaxy";
  const isBeam = resolved === "beam";
  const isDiagonalRay = resolved === "diagonal_ray";
  const isHorizon = resolved === "horizon";
  const isRipple = resolved === "ripple";
  const isInverse = resolved === "inverse_foil";
  const isAlternate = resolved === "alternate_color";
  const is3d = resolved === "3d";
  const isTopography = resolved === "topography";
  const isRisingStar = resolved === "rising_star";
  const livePose = inheritPose || trackPointer;
  const rootStyle = {
    ...(poseStyleFromVars(livePose ? null : pose, opacities) || {}),
    ...(galaxyVars || {}),
  };

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      className={[styles.foilOverlay, styles.aiHoloFx, className]
        .filter(Boolean)
        .join(" ")}
      style={rootStyle}
    >
      {isRipple ? (
        <svg className={styles.holoRippleSvg} aria-hidden="true">
          <defs>
            <filter
              id={rippleFilterId}
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feTurbulence
                ref={rippleNoiseRef}
                type="fractalNoise"
                baseFrequency="0.016 0.022"
                numOctaves="4"
                seed={rippleSeed}
                result="noise"
              />
              <feDisplacementMap
                ref={rippleDispRef}
                in="SourceGraphic"
                in2="noise"
                scale="36"
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          </defs>
        </svg>
      ) : null}
      {isRainbow || isInverse ? (
        <div className={`${styles.holoShine} ${styles.holoShineBaseRainbow}`}>
          <div className={styles.holoShineBaseRainbowMetal} />
        </div>
      ) : null}
      {isGrayscale ? (
        <div className={`${styles.holoShine} ${styles.holoShineMetallicGlitter}`}>
          <div className={styles.holoShineMetallicGlitterSpark} />
        </div>
      ) : null}
      {isAlternate ? (
        <div className={`${styles.holoShine} ${styles.holoShineRainbowGlitter}`}>
          <div className={styles.holoShineRainbowGlitterSpark} />
        </div>
      ) : null}
      {isGalaxy ? (
        <div className={`${styles.holoShine} ${styles.holoShineGalaxy}`}>
          <div className={styles.holoShineGalaxyCosmos} />
          <div className={styles.holoGalaxyGlow} />
        </div>
      ) : null}
      {isBeam ? (
        <div className={`${styles.holoShine} ${styles.holoShineBeam}`}>
          <div className={styles.holoShineBeamBars} />
          <div className={styles.holoShineBeamSpot} />
        </div>
      ) : null}
      {isDiagonalRay ? (
        <div className={`${styles.holoShine} ${styles.holoShineDiagonalRay}`}>
          <div className={styles.holoShineDiagonalRayBars} />
          <div className={styles.holoShineDiagonalRayGlow} />
        </div>
      ) : null}
      {isHorizon ? (
        <div className={`${styles.holoShine} ${styles.holoShineHorizon}`}>
          <div className={styles.holoShineHorizonBars} />
          <div className={styles.holoShineHorizonSpot} />
        </div>
      ) : null}
      {is3d ? (
        <div
          className={`${styles.holoShine} ${styles.holoShineMetallic} ${styles.holoShine3d}`}
        >
          <div className={styles.holoShineMetallicMetal} />
        </div>
      ) : null}
      {isTopography && topography ? (
        <div className={`${styles.holoShine} ${styles.holoShineTopography}`}>
          <TopographyLines pattern={topography} styles={styles} />
          {quality === "maximum" ? (
            <TopographyLines
              pattern={topography}
              styles={styles}
              className={styles.holoTopographyGlow}
            />
          ) : null}
          <div className={styles.holoTopographySheen} />
        </div>
      ) : null}
      {isRisingStar ? (
        <div className={`${styles.holoShine} ${styles.holoShineRisingStar}`}>
          <div ref={risingStarLayerRef} className={styles.holoRisingStarLayer} />
        </div>
      ) : null}
      {!resolved ? (
        <div className={`${styles.holoShine} ${styles.holoShineSoft}`} />
      ) : null}
      <div
        className={`${styles.holoGlare} ${
          isRainbow || isInverse
            ? styles.holoGlareBaseRainbow
            : isGrayscale
              ? styles.holoGlareMetallicGlitter
              : isAlternate
                ? styles.holoGlareRainbowGlitter
                : isGalaxy
                  ? styles.holoGlareGalaxy
                  : isBeam
                    ? styles.holoGlareBeam
                    : isDiagonalRay
                      ? styles.holoGlareDiagonalRay
                      : isHorizon
                        ? styles.holoGlareHorizon
                        : isRipple
                          ? styles.holoGlareRipple
                          : isTopography
                            ? styles.holoGlareTopography
                            : isRisingStar
                              ? styles.holoGlareRisingStar
                              : styles.holoGlareBase
        }`}
      />
    </div>
  );
}
